import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useEvent } from '@/context/EventContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Home, Download, Calendar, Loader2, Trophy, Printer, Share2, Copy, Check, X, BarChart3 } from 'lucide-react';
import { BracketBoard } from '@/components/bracket/BracketBoard';
import { buildBracketModel, describeEvent, isEliminationFormat } from '@/lib/bracket/model';
import { isCricket } from '@/lib/cricket';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const slugify = (s) => (s || 'bracket').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'bracket';

const EmptyState = ({ title, body, action }) => (
  <Card className="border-slate-200 max-w-xl mx-auto">
    <CardContent className="p-8 text-center">
      <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
      <h2 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h2>
      <p className="text-slate-500">{body}</p>
      {action}
    </CardContent>
  </Card>
);

const ShareModal = ({ shareUrl, copied, onCopy, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <Card className="w-full max-w-md mx-4 border-slate-200 shadow-xl">
      <div className="flex items-center justify-between p-4 pb-2">
        <h3 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>Share Bracket</h3>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="share-modal-close-btn"><X className="w-4 h-4" /></Button>
      </div>
      <CardContent className="space-y-4 pt-0">
        <p className="text-sm text-slate-600">Anyone with this link can view the schedule and bracket (read-only).</p>
        <div className="flex gap-2">
          <Input value={shareUrl} readOnly className="font-mono text-sm bg-slate-50" data-testid="share-url-input" />
          <Button variant="outline" onClick={onCopy} data-testid="copy-share-link-btn">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <Button className="w-full bg-[#00247D] hover:bg-[#001A5C]" onClick={onClose} data-testid="share-modal-done-btn">Done</Button>
      </CardContent>
    </Card>
  </div>
);

export default function BracketView() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentEvent, fetchEvent, loading } = useEvent();
  const { isAuthenticated } = useAuth();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  useEffect(() => {
    if (eventId && currentEvent?.id !== eventId) {
      fetchEvent(eventId).catch(() => { toast.error('Failed to load event'); navigate('/'); });
    }
  }, [eventId, currentEvent, fetchEvent, navigate]);

  const model = useMemo(
    () => (currentEvent ? buildBracketModel({ format: currentEvent.format, schedule: currentEvent.schedule }) : null),
    [currentEvent],
  );
  const isElim = isEliminationFormat(currentEvent?.format, currentEvent?.playoffs);
  const showCourt = (currentEvent?.num_courts || 1) > 1;

  const handleScoreSave = async (matchId, scoreA, scoreB, extra) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/events/${eventId}/schedule/${matchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ score_a: scoreA, score_b: scoreB, ...extra }),
      });
      if (response.ok) {
        if (scoreA === scoreB) toast.warning('Score saved. It is a tie, so no team advanced.');
        else toast.success('Score saved! Bracket updated.');
        fetchEvent(eventId);
        return true;
      }
      const data = await response.json().catch(() => ({}));
      toast.error(data.detail || 'Failed to save score');
    } catch { toast.error('Failed to save score'); }
    return false;
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      if (currentEvent?.share_token) {
        setShareUrl(`${window.location.origin}/share/${currentEvent.share_token}`);
        setShowShareModal(true);
      } else {
        const response = await fetch(`${API_URL}/api/events/${eventId}/share`, {
          method: 'POST', credentials: 'include', headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` },
        });
        const data = await response.json();
        if (data.success) {
          setShareUrl(`${window.location.origin}${data.share_url}`);
          setShowShareModal(true);
          fetchEvent(eventId);
          toast.success('Share link created!');
        }
      }
    } catch { toast.error('Failed to create share link'); } finally { setShareLoading(false); }
  };

  const copyToClipboard = () => {
    const name = (currentEvent?.name || '').trim();
    navigator.clipboard.writeText(name ? `${name}\n${shareUrl}` : shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  if (loading && !currentEvent) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00247D]" /></div>;
  if (!currentEvent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-600 mb-4">Event not found</p>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-2 min-h-16 py-2">
            <div className="flex items-center gap-4">
              <Button variant="ghost" data-testid="back-home-btn" onClick={() => navigate('/')} className="text-slate-600"><Home className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>BRACKET VIEW</h1>
                <p className="text-xs text-slate-500">{currentEvent.name}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" data-testid="print-bracket-btn" onClick={() => window.print()} disabled={!model}><Printer className="w-4 h-4 mr-2" />Print</Button>
              <Button variant="outline" data-testid="share-bracket-btn" onClick={handleShare} disabled={shareLoading}>
                {shareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}Share
              </Button>
              <Button variant="outline" data-testid="view-dashboard-btn" onClick={() => navigate('/my-schedules')}><BarChart3 className="w-4 h-4 mr-2" />Dashboard</Button>
              <Button variant="outline" data-testid="view-schedule-btn" onClick={() => navigate(`/schedule/${eventId}`)}><Calendar className="w-4 h-4 mr-2" />Schedule</Button>
              <Button data-testid="export-btn" onClick={() => navigate(`/export/${eventId}`)} className="bg-[#00247D] hover:bg-[#001A5C]"><Download className="w-4 h-4 mr-2" />All Exports</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-6">
        {!isElim ? (
          <EmptyState title="NO BRACKET FOR THIS FORMAT" body="This event uses Round Robin format."
            action={<Button className="mt-6 bg-[#00247D] hover:bg-[#001A5C]" onClick={() => navigate(`/schedule/${eventId}`)}>View Schedule</Button>} />
        ) : !model ? (
          <EmptyState title="PLAYOFF BRACKETS NOT YET GENERATED" body="Complete the round robin stage to generate playoff brackets." />
        ) : (
          <BracketBoard model={model} title={currentEvent.name?.toUpperCase()} subtitle={describeEvent(currentEvent)}
            eventLogo={currentEvent.event_logo ? `${API_URL}/api/files/${currentEvent.event_logo}` : null}
            logos={currentEvent.team_logos || {}} editable={isAuthenticated} onScoreSave={handleScoreSave} cricket={isCricket(currentEvent.sport)}
            storageKey={`bracket-layout:${eventId}`} fileName={`${slugify(currentEvent.name)}-bracket`} showCourt={showCourt} />
        )}
      </main>

      {showShareModal && <ShareModal shareUrl={shareUrl} copied={copied} onCopy={copyToClipboard} onClose={() => setShowShareModal(false)} />}
    </div>
  );
}
